import { useOrderActions } from '../../../../utilities/hooks/useOrderActions'

import { constants } from '@blocktickets/shared-utils'

import {
  capitalizeString,
  formatCurrency,
  formatDateTime,
  formatNumber,
  getTimezoneDate,
  getSinglarOrPluralWord,
  formatTicketGroups,
  getOrderSeatLocationTickets,
  getOrderTicketQuantity,
  sourceStatuses,
  timezones,
  getPaymentOption,
} from '../../../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import Dropdown from 'react-bootstrap/Dropdown'

import { OrderBadges } from './OrderBadges'
import { EditDeleteDropdown } from '../../../EditDeleteDropdown'
import { TicketRow } from './TicketRow'

import { useModal } from '../../../../providers/ModalProvider/ModalProvider'
import AssignTicketsModalContentV2 from '../../../../providers/AssignToProvider/AssignTicketsModalContent/AssignTicketsModalContentV2'
import boxesIcon from '../../../../assets/icons/boxes.svg'

import './orderSummary.scss'

function parseOrderDetails(details) {
  if (!details) return {}
  if (typeof details === 'string') {
    try {
      return JSON.parse(details)
    } catch {
      return {}
    }
  }
  return details
}

function getSourceOrderCustomFeeIds(order) {
  const details = parseOrderDetails(order?.details)
  const fromDetails = details?.customFeeIds
  if (Array.isArray(fromDetails) && fromDetails.length) {
    return fromDetails
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0)
  }

  const fromRelation = order?.custom_fees
  if (Array.isArray(fromRelation) && fromRelation.length) {
    return fromRelation
      .map((fee) => Number(fee?.id ?? fee?.attributes?.id))
      .filter((n) => Number.isFinite(n) && n > 0)
  }

  return []
}

function getSupplementalCustomFeeNames(order) {
  const details = parseOrderDetails(order?.details)
  const lines = details?.supplementalCustomFee?.lines
  const names = Array.isArray(lines)
    ? lines.map((line) => String(line?.name || '').trim()).filter(Boolean)
    : []
  if (names.length) return names

  const fromRelation = order?.custom_fees
  if (Array.isArray(fromRelation)) {
    return fromRelation
      .map((fee) => String(fee?.name ?? fee?.attributes?.name ?? '').trim())
      .filter(Boolean)
  }

  return []
}

function getSourceOrderBuyerTypeId(order) {
  const details = parseOrderDetails(order?.details)
  const fromDetails = details?.selectedBuyerType
  if (fromDetails != null && String(fromDetails).trim() !== '') {
    const id = Number(fromDetails)
    if (Number.isFinite(id) && id > 0) return id
  }

  const buyerType = order?.buyer_type
  if (buyerType != null) {
    const id = Number(
      typeof buyerType === 'object'
        ? (buyerType?.id ?? buyerType?.data?.id)
        : buyerType
    )
    if (Number.isFinite(id) && id > 0) return id
  }

  return undefined
}

export default function OrderSummary({
  order,
  runSearch,
  canManageOrders = true,
  showDatePaid = false,
}) {
  const orderActions = useOrderActions(runSearch)

  const { ORDER_STATUS, ORDER_SOURCE } = constants

  const getColor = (status, source, ticketsSent) => {
    // refunded/released - light
    // completeFromAssignment - success
    // tickets sent - success
    // default - tertiary
    if (status === ORDER_STATUS.REFUNDED || status === ORDER_STATUS.RELEASED)
      return 'light'
    if (status === ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT || ticketsSent)
      return 'success'
    return 'tertiary'
  }

  const getName = (order) => {
    if (order?.users_permissions_user) {
      return order.users_permissions_user
    }

    return {
      firstName: order?.firstName || 'NO',
      lastName: order?.lastName || 'USER',
      email: order?.email,
    }
  }

  const getObj = (order) => {
    if (order?.package) {
      return order.package
    }

    if (order?.event) {
      return order.event
    }

    if (order?.flex_pack) {
      return order.flex_pack
    }

    if (order?.access_pass_template) {
      return order.access_pass_template
    }

    return { name: 'NA' }
  }

  const getNameInitials = (user) => {
    if (user) {
      return `${user?.firstName?.charAt(0)}${user?.lastName?.charAt(0)}`
    }
  }

  const getTitle = (order, orderColor) => {
    if (order.event && !order.package) {
      return (
        <Stack direction="vertical" gap={1}>
          <Card.Title>{obj?.name}</Card.Title>
          <span className="d-block small">
            {formatDateTime(getTimezoneDate(obj?.start, timezone))}
          </span>
        </Stack>
      )
    }

    return (
      <Stack direction="vertical" gap={1}>
        <Card.Title>{obj?.name}</Card.Title>
        <Stack
          direction="horizontal"
          gap={2}
          className={`package-stack package-stack--${orderColor}`}
        >
          {!order?.flex_pack && (
            <>
              <svg
                width="18"
                height="18"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.5373 7.7807C18.5439 7.41946 17.455 7.41946 16.4616 7.7807L5.88867 11.6254L16.4616 15.4701C17.455 15.8313 18.5439 15.8313 19.5373 15.4701L30.1102 11.6254L19.5373 7.7807ZM15.4363 4.96132C17.092 4.35925 18.9068 4.35925 20.5625 4.96132L31.1354 8.80601C33.7684 9.76348 33.7684 13.4873 31.1354 14.4448L20.5625 18.2895C18.9068 18.8915 17.092 18.8915 15.4363 18.2895L4.86344 14.4448C2.23041 13.4873 2.23042 9.76347 4.86344 8.80601L15.4363 4.96132Z"
                  fill="#BCBFC4"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.08709 18.5936C3.36462 17.813 4.22237 17.4053 5.00292 17.6828L16.9954 21.9468C17.6455 22.1779 18.3554 22.1779 19.0054 21.9468L30.9979 17.6828C31.7785 17.4053 32.6362 17.813 32.9137 18.5936C33.1913 19.3742 32.7835 20.2319 32.0029 20.5094L20.0105 24.7734C18.7103 25.2357 17.2905 25.2357 15.9904 24.7734L3.99789 20.5094C3.21734 20.2319 2.80956 19.3742 3.08709 18.5936Z"
                  fill="#BCBFC4"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M3.08709 24.9686C3.36462 24.188 4.22237 23.7803 5.00292 24.0578L16.9954 28.3218C17.6455 28.5529 18.3554 28.5529 19.0054 28.3218L30.9979 24.0578C31.7785 23.7803 32.6362 24.188 32.9137 24.9686C33.1913 25.7492 32.7835 26.6069 32.0029 26.8844L20.0105 31.1484C18.7103 31.6107 17.2905 31.6107 15.9904 31.1484L3.99789 26.8844C3.21734 26.6069 2.80956 25.7492 3.08709 24.9686Z"
                  fill="#BCBFC4"
                />
              </svg>
              <span className="d-block small">
                <span>
                  {obj?.events?.length > 0
                    ? `${formatNumber(`${obj?.events.length}`)}`
                    : 'N/A'}
                </span>{' '}
                <span>
                  {getSinglarOrPluralWord(
                    obj?.events?.length,
                    'Individual Game'
                  )}
                </span>
              </span>
            </>
          )}
        </Stack>
      </Stack>
    )
  }

  const user = getName(order)
  const obj = getObj(order)
  const seatLocationTickets = getOrderSeatLocationTickets(order)
  const ticketQuantity = getOrderTicketQuantity(order)
  const orderColor = getColor(order.status, order.source, order.emailSent)
  const orderSource =
    sourceStatuses[
      order?.source || (order?.boxOffice && 'box_office') || 'website'
    ]
  const groupedFormattedTickets = formatTicketGroups(seatLocationTickets)
  const timezone = timezones(obj?.venue?.timezone)
  const isSupplementalCustomFee = order?.type === 'customFeeAddition'
  const supplementalCustomFeeNames = isSupplementalCustomFee
    ? getSupplementalCustomFeeNames(order)
    : []
  const supplementalCustomFeeLabel = supplementalCustomFeeNames.length
    ? `Custom fee: ${supplementalCustomFeeNames.join(', ')}`
    : 'Supplemental custom fee'

  return (
    <li>
      <Card
        body
        className={`order-summary-card card--light card--md card-with-border--${orderColor}`}
      >
        <Stack
          direction="horizontal"
          className="align-items-start justify-content-between"
        >
          <Stack
            direction="horizontal"
            gap={3}
            className="align-items-start flex-fill"
          >
            <div className={`user-profile user-profile--${orderColor}`}>
              <span>{getNameInitials(user)}</span>
            </div>
            <Stack gap={1}>
              <Stack
                direction="horizontal"
                gap={3}
                className="align-items-center"
              >
                <span className="heading m-0">
                  {capitalizeString(user.firstName)}{' '}
                  {capitalizeString(user.lastName)}
                </span>
                <OrderBadges order={order} />
              </Stack>
              <span className="d-block small">{user.email}</span>
              <Stack
                direction="horizontal"
                gap={3}
                className="align-items-center"
              >
                <span className="d-block small">
                  Processed on:{' '}
                  {formatDateTime(
                    getTimezoneDate(order?.processedAt, timezone)
                  )}
                </span>
                {showDatePaid && order?.datePaid && (
                  <span className="d-block small">
                    Date paid: {formatDateTime(order.datePaid, 'dateOnlyMdY')}
                  </span>
                )}
                <Stack
                  direction="horizontal"
                  gap={1}
                  className="align-items-center"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M8.00001 1.34332C4.31811 1.34332 1.33334 4.32809 1.33334 8.00999C1.33334 11.6919 4.31811 14.6767 8.00001 14.6767C11.6819 14.6767 14.6667 11.6919 14.6667 8.00999C14.6667 4.32809 11.6819 1.34332 8.00001 1.34332ZM2.8347 6.67666C2.72501 7.10282 2.66668 7.54959 2.66668 8.00999C2.66668 8.47039 2.72501 8.91716 2.8347 9.34332H4.72956C4.68819 8.91137 4.66668 8.46524 4.66668 8.00999C4.66668 7.55473 4.68819 7.10861 4.72956 6.67666H2.8347ZM3.38018 5.34332H4.92879C5.07011 4.66099 5.26509 4.0368 5.50518 3.49659C5.54536 3.4062 5.5871 3.31747 5.63042 3.23065C4.68862 3.69849 3.90474 4.43652 3.38018 5.34332ZM6.06965 6.67666C6.02435 7.10171 6.00001 7.54812 6.00001 8.00999C6.00001 8.47186 6.02435 8.91827 6.06965 9.34332H9.93037C9.97567 8.91827 10 8.47186 10 8.00999C10 7.54812 9.97567 7.10171 9.93037 6.67666H6.06965ZM9.70559 5.34332H6.29444C6.41022 4.85509 6.55587 4.41549 6.7236 4.03811C6.94226 3.54613 7.18535 3.18927 7.42161 2.96443C7.65444 2.74286 7.84874 2.67666 8.00001 2.67666C8.15128 2.67666 8.34558 2.74286 8.57841 2.96443C8.81467 3.18927 9.05777 3.54613 9.27642 4.03811C9.44415 4.41549 9.5898 4.85509 9.70559 5.34332ZM11.2705 6.67666C11.3118 7.10861 11.3333 7.55473 11.3333 8.00999C11.3333 8.46524 11.3118 8.91137 11.2705 9.34332H13.1653C13.275 8.91716 13.3333 8.47039 13.3333 8.00999C13.3333 7.54959 13.275 7.10282 13.1653 6.67666H11.2705ZM12.6198 5.34332H11.0712C10.9299 4.66099 10.7349 4.0368 10.4948 3.49659C10.4547 3.4062 10.4129 3.31747 10.3696 3.23065C11.3114 3.69849 12.0953 4.43652 12.6198 5.34332ZM5.63042 12.7893C4.68862 12.3215 3.90474 11.5835 3.38018 10.6767H4.92879C5.07011 11.359 5.26509 11.9832 5.50518 12.5234C5.54536 12.6138 5.5871 12.7025 5.63042 12.7893ZM6.7236 11.9819C6.55587 11.6045 6.41022 11.1649 6.29444 10.6767H9.70559C9.5898 11.1649 9.44415 11.6045 9.27642 11.9819C9.05777 12.4738 8.81467 12.8307 8.57841 13.0555C8.34558 13.2771 8.15128 13.3433 8.00001 13.3433C7.84874 13.3433 7.65444 13.2771 7.42161 13.0555C7.18535 12.8307 6.94226 12.4738 6.7236 11.9819ZM10.4948 12.5234C10.7349 11.9832 10.9299 11.359 11.0712 10.6767H12.6198C12.0953 11.5835 11.3114 12.3215 10.3696 12.7893C10.4129 12.7025 10.4547 12.6138 10.4948 12.5234Z"
                      fill="#777E91"
                    />
                  </svg>
                  <span className="d-block small">{orderSource?.label} </span>
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="horizontal" gap={3} className="align-items-start">
            <Stack className="flex-fill align-items-end" gap={1}>
              <span className="heading m-0">
                {formatCurrency(Number(order?.total))}
              </span>
              {/* <div className='payment-method'>
                <span className='small'>
                  {order?.paymentMethodType} {order?.last4}
                </span>
              </div> */}
              <span className="small">Order ID {order?.orderId}</span>
            </Stack>
            {canManageOrders && (
              <span data-testid="order-actions-dropdown" className="d-inline">
                <OrderActionsDropdown
                  orderActions={orderActions}
                  order={order}
                  runSearch={runSearch}
                  isFlexPack={order.flex_pack}
                />
              </span>
            )}
          </Stack>
        </Stack>
        <Stack
          direction="horizontal"
          className="justify-content-between align-items-center"
        >
          {getTitle(order, orderColor)}
          <small>
            {isSupplementalCustomFee
              ? supplementalCustomFeeLabel
              : `${formatNumber(ticketQuantity)} ${getSinglarOrPluralWord(
                  ticketQuantity,
                  order?.flex_pack ? 'Voucher' : 'Ticket'
                )}`}
          </small>
        </Stack>
        {groupedFormattedTickets && groupedFormattedTickets.length > 0 && (
          <Stack gap={1} style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {groupedFormattedTickets.map((group, i) => (
              <TicketRow group={group} key={i} />
            ))}
          </Stack>
        )}

        {/* Need to add something like transfers_from_order to main order before we can do this */}
        {/* <Stack className="transfer-to-block position-relative ms-3" gap={3}>
                    <h3 className='small small-bold'>Transferred to</h3>
                    <Card body className='card--sm' style={{ maxHeight: '400px', overflowY: 'auto'}}>
                        <div className="list-table four-col" role="table">
                            <div className="flex-row list-table-header" role="rowgroup">
                                <div className='list-table-col list-table-col-header list-table-col-header--text-upper list-table-col-header--dark-gray lg' role="columnheader">
                                    <span>Ticket Holder</span>
                                </div>
                                <div className='list-table-col list-table-col-header list-table-col-header--dark-gray text-left list-table-col-header--text-upper' role="columnheader">
                                    <span>Offer</span>
                                </div>
                                <div className="list-table-col list-table-col-header list-table-col-header--dark-gray text-left list-table-col-header--text-upper md" role="columnheader">
                                    <span>Section</span>
                                </div>
                                <div className="list-table-col list-table-col-header list-table-col-header--dark-gray text-left list-table-col-header--text-upper md" role="columnheader">
                                    <span>Row</span>
                                </div>
                                <div className="list-table-col list-table-col-header list-table-col-header--dark-gray md text-left list-table-col-header--text-upper" role="columnheader">
                                    <span>Seat</span>
                                </div>
                                <div className="list-table-col list-table-col-header list-table-col-header--dark-gray md text-left list-table-col-header--text-upper" role="columnheader">
                                    <span>Paid</span>
                                </div>
                            </div>
                            {order.tickets.map((ticket, i) => {
                                return <TicketRow user={user} ticket={ticket} key={i} />
                            })}
                        </div>
                    </Card>
                </Stack> */}
        {/* <div className='flex btn-group-flex justify-content-start mt-0'>
                    <Button size="sm" onClick={() => openViewProfile(order)}>View buyer profile</Button>
                    <Button size="sm" onClick={() => openViewOrder(order)}>View order</Button>
                    <Button size="sm" onClick={() => openViewAssociatedTo(order)}>View associated to</Button>
                    <Button size="sm" variant='outline-light'>Refund order</Button>
                </div> */}
      </Card>
    </li>
  )
}

export function OrderActionsDropdown({
  orderActions,
  order,
  runSearch,
  isViewingProfile = false,
  isFlexPack,
  popperStrategy,
}) {
  const { openModal } = useModal()

  const { ORDER_STATUS } = constants

  const hasPaymentIntent =
    order?.paymentIntentId && String(order.paymentIntentId).trim() !== ''
  const isAccessPass = Boolean(
    order?.access_pass_template || order?.access_passes?.length
  )
  const isAccessPassDelivery = Boolean(
    isAccessPass && !order?.package && !order?.packageOrder
  )
  // Transfers and exchanges copy packageOrder off the order the seats came out
  // of, so the flag alone says nothing about what this order delivers. Only a
  // linked package or season seats of its own make it a package delivery.
  const isPackageDelivery = Boolean(
    order?.package || order?.package_tickets?.length
  )
  // Tickets and vouchers can be printed and emailed while a balance is still
  // owed; collecting the balance is tracked separately from delivery.
  const hasUnpaidBalance = Boolean(
    order?.status === ORDER_STATUS.OPEN ||
    order?.status === ORDER_STATUS.PAST_DUE
  )
  // Reserved season packages can still be emailed while the hold is in place.
  // Reserved event tickets stay blocked.
  const canSendDeliveryEmail = Boolean(
    order?.status === ORDER_STATUS.COMPLETE ||
    order?.status === ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT ||
    order?.status === ORDER_STATUS.COMPLETE_FROM_EXCHANGE ||
    order?.status === ORDER_STATUS.PLAN ||
    hasUnpaidBalance ||
    (order?.status === ORDER_STATUS.RESERVED && isPackageDelivery)
  )
  const isSupplementalCustomFee = order?.type === 'customFeeAddition'
  const canAddSupplementalCustomFee = Boolean(
    !order?.event &&
    order?.package &&
    !isSupplementalCustomFee &&
    String(order?.status || '').startsWith(ORDER_STATUS.COMPLETE)
  )

  const getOrderSeatmapData = (tickets = []) => {
    const seats = {}
    const sections = {}
    const selectedSeats = {}
    const selectedGASeats = {}

    tickets.forEach((ticket) => {
      const isGeneralAdmission = Boolean(
        ticket?.generalAdmission ?? ticket?.general_admission ?? ticket?.GA
      )
      const seatId =
        ticket?.seatId || ticket?.seat_id || ticket?.seat?.id || ticket?.id
      const sectionId =
        ticket?.sectionId ||
        ticket?.section_id ||
        ticket?.section?.id ||
        ticket?.section?.sectionId ||
        ticket?.sectionNumber ||
        ticket?.section_number
      const sectionNumber =
        ticket?.sectionNumber ||
        ticket?.section_number ||
        ticket?.section?.sectionNumber ||
        ticket?.section?.number ||
        sectionId

      if (isGeneralAdmission) {
        if (!sectionId || !seatId) return
        if (!selectedGASeats[sectionId]) selectedGASeats[sectionId] = []
        if (!selectedGASeats[sectionId].includes(seatId)) {
          selectedGASeats[sectionId].push(seatId)
        }
        if (!sections[sectionId]) {
          sections[sectionId] = {
            sectionId,
            sectionNumber,
          }
        }
        return
      }

      if (!seatId) return
      selectedSeats[seatId] = seatId
      seats[seatId] = {
        sectionNumber,
        rowNumber: ticket?.rowNumber || ticket?.row_number,
        seatNumber: ticket?.seatNumber || ticket?.seat_number,
      }
    })

    const selectedSeatIds = {}
    if (Object.keys(selectedSeats).length > 0) {
      selectedSeatIds.seats = selectedSeats
    }
    if (Object.keys(selectedGASeats).length > 0) {
      selectedSeatIds.gaSeats = selectedGASeats
    }

    return {
      selectedSeatIds,
      data: {
        seats,
        sections,
      },
    }
  }

  const getInitialAssignModalProps = (
    tickets = [],
    eventPackage = undefined
  ) => {
    const { selectedSeatIds, data } = getOrderSeatmapData(tickets)
    const initialAssignTo = {
      firstName:
        order?.firstName || order?.users_permissions_user?.firstName || '',
      lastName:
        order?.lastName || order?.users_permissions_user?.lastName || '',
      email: order?.email || order?.users_permissions_user?.email || '',
      phoneNumber:
        order?.phoneNumber ||
        order?.users_permissions_user?.phoneNumber ||
        null,
      organizationName: order?.purchasedByOrganizationName || '',
    }
    const paymentMethodMap = {
      card: 'CARD',
      credit_card: 'CARD',
      'credit-card': 'CARD',
      cc: 'CARD',
      invoice: 'INVOICE',
      pos: 'POS',
      'pos-terminal': 'POS',
      cash: 'CASH',
      check: 'CHECK',
      team_credit: 'TEAM_CREDIT',
      'team-credit': 'TEAM_CREDIT',
      external: 'EXTERNAL',
      payroll_deduction: 'PAYROLL_DEDUCTION',
      'payroll-deduction': 'PAYROLL_DEDUCTION',
      e_transfer: 'E_TRANSFER',
      'e-transfer': 'E_TRANSFER',
      voucher: 'VOUCHER',
      comp: 'COMP',
      reserved: 'RESERVED',
    }
    const normalizedPaymentMethod = String(
      order?.paymentMethodType || order?.paymentMethod || ''
    )
      .trim()
      .toLowerCase()
    const initialPaymentMethod = paymentMethodMap[normalizedPaymentMethod]
    const sourceOrderTotal = Number(
      order?.total ?? order?.gross ?? order?.details?.groupTicketsPrice ?? 0
    )
    const sourceOrderPricingTier =
      order?.pricingTier ||
      order?.pricing_tier ||
      order?.details?.pricingTier ||
      order?.details?.pricing_tier ||
      parseOrderDetails(order?.details)?.pricingTier ||
      parseOrderDetails(order?.details)?.pricing_tier
    const sourceOrderCustomFeeIds = getSourceOrderCustomFeeIds(order)
    const sourceOrderBuyerTypeId = getSourceOrderBuyerTypeId(order)
    const initialExistingUser = initialAssignTo?.email
      ? {
          label:
            `${initialAssignTo.firstName} ${initialAssignTo.lastName}`.trim(),
          firstName: initialAssignTo.firstName,
          lastName: initialAssignTo.lastName,
          email: initialAssignTo.email,
          phoneNumber: initialAssignTo.phoneNumber,
          custom: initialAssignTo.email,
        }
      : undefined

    return {
      event: order?.event,
      eventPackage,
      selectedSeatIds,
      data,
      initialAssignTo,
      initialExistingUser,
      initialPaymentMethod,
      sourceOrderId: order?.id,
      sourceOrderTotal: Number.isFinite(sourceOrderTotal)
        ? sourceOrderTotal
        : undefined,
      sourceOrderPricingTier,
      sourceOrderCustomFeeIds,
      sourceOrderBuyerTypeId,
      reloadAssignments: runSearch,
    }
  }

  const openRenewModal = () => {
    const renewPackage = {
      ...(order?.package || {}),
      uuid:
        order?.package?.uuid ||
        order?.packageUuid ||
        order?.package_uuid ||
        order?.packageUUID,
    }
    openModal(
      AssignTicketsModalContentV2,
      getInitialAssignModalProps(order?.package_tickets || [], renewPackage),
      { className: 'modal-xl', contentClassName: 'h-md' }
    )
  }

  const openCompleteAssignmentModal = () => {
    const tickets = order?.tickets || []

    openModal(
      AssignTicketsModalContentV2,
      getInitialAssignModalProps(tickets),
      { className: 'modal-xl', contentClassName: 'h-md' }
    )
  }

  const invoicePaymentMethod = String(
    order?.paymentMethodType || order?.paymentOption || ''
  )
    .trim()
    .toLowerCase()
  const canPayInvoiceByCashOrCheck =
    (order?.status === ORDER_STATUS.OPEN ||
      order?.status === ORDER_STATUS.PAST_DUE ||
      order?.status === ORDER_STATUS.INVOICE_SENT) &&
    (invoicePaymentMethod === 'invoice' ||
      invoicePaymentMethod === 'invoice_sent')

  const payInvoiceByCashOrCheck = canPayInvoiceByCashOrCheck ? (
    <li className="w-100 order-3">
      <Dropdown.Item
        as="button"
        className="btn-complete"
        onClick={() =>
          orderActions.openMarkAsPaid(order, runSearch, {
            cashOrCheckOnly: true,
          })
        }
      >
        Pay by cash or check
      </Dropdown.Item>
    </li>
  ) : null

  return (
    <EditDeleteDropdown
      canEdit={false}
      canDelete={false}
      canDisable={false}
      popperStrategy={popperStrategy}
    >
      {isFlexPack ? (
        <>
          {/* show if order is not refunded and not released */}
          {order?.status !== ORDER_STATUS.REFUNDED &&
            order?.status !== ORDER_STATUS.RELEASED && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-view"
                  onClick={() => orderActions.openViewVouchers(order?.vouchers)}
                >
                  View vouchers
                </Dropdown.Item>
              </li>
            )}
          {/* show if order is
              - a flex pack original purchase (not a voucher redemption)
              - has vouchers
              - is complete, payment plan, or still owes a balance (open, past due)
              - won't show if refunded, released, reserved */}
          {!order?.voucher &&
            order?.vouchers?.length > 0 &&
            (order?.status === ORDER_STATUS.COMPLETE ||
              order?.status === ORDER_STATUS.PLAN ||
              hasUnpaidBalance) && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-print"
                  onClick={() => orderActions.openPrintVouchers(order)}
                >
                  Print vouchers
                </Dropdown.Item>
              </li>
            )}
          {/* Void flex pack purchase/assignment: refund if PI present, else cancel + remove vouchers */}
          {!order?.voucher &&
            order?.status !== ORDER_STATUS.REFUNDED &&
            order?.status !== ORDER_STATUS.RELEASED &&
            (order?.status === ORDER_STATUS.COMPLETE ||
              order?.status === ORDER_STATUS.PLAN ||
              order?.status === ORDER_STATUS.OPEN ||
              order?.status === ORDER_STATUS.PAST_DUE) && (
              <li className="w-100 order-last">
                <Dropdown.Item
                  as="button"
                  className="btn-refund"
                  onClick={() => orderActions.openVoidOrder(order)}
                >
                  Void order
                </Dropdown.Item>
              </li>
            )}
          {payInvoiceByCashOrCheck}
        </>
      ) : (
        <>
          {/* reserved package/event actions */}
          {order?.status === ORDER_STATUS.RESERVED && (
            <>
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-profile"
                  onClick={() =>
                    order?.event
                      ? openCompleteAssignmentModal()
                      : openRenewModal()
                  }
                >
                  {order?.event
                    ? 'Complete Assignment'
                    : 'Renew Season Tickets'}
                </Dropdown.Item>
              </li>
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-refund"
                  onClick={() => orderActions.openCancelOrder(order)}
                >
                  Cancel
                </Dropdown.Item>
              </li>
            </>
          )}
          {/* allow relocating seats on completed season package orders as well */}
          {!order?.event &&
            Array.isArray(order?.package_tickets) &&
            order.package_tickets.length > 0 &&
            (order?.status === ORDER_STATUS.RESERVED ||
              order?.status === ORDER_STATUS.COMPLETE) && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="icon-button-lg"
                  onClick={() => orderActions.openRelocateSeats(order)}
                >
                  <img
                    src={boxesIcon}
                    alt=""
                    aria-hidden="true"
                    width={24}
                    height={24}
                    className="flex-shrink-0"
                  />
                  Relocate Seats
                </Dropdown.Item>
              </li>
            )}
          {/* add seats to existing season package orders */}
          {!order?.event &&
            order?.package &&
            (order?.status === ORDER_STATUS.RESERVED ||
              order?.status === ORDER_STATUS.COMPLETE ||
              order?.status === ORDER_STATUS.PLAN ||
              order?.status === ORDER_STATUS.OPEN) && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="icon-button-lg"
                  onClick={() => orderActions.openAddSeats(order)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                    className="flex-shrink-0"
                  >
                    <path
                      d="M4 8.5C4 7.67157 4.67157 7 5.5 7H8.5C9.32843 7 10 7.67157 10 8.5V11.5C10 12.3284 9.32843 13 8.5 13H5.5C4.67157 13 4 12.3284 4 11.5V8.5Z"
                      stroke="#141416"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M14 8.5C14 7.67157 14.6716 7 15.5 7H18.5C19.3284 7 20 7.67157 20 8.5V11.5C20 12.3284 19.3284 13 18.5 13H15.5C14.6716 13 14 12.3284 14 11.5V8.5Z"
                      stroke="#141416"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M12 5V19"
                      stroke="#141416"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M5 12H19"
                      stroke="#141416"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Add Seats
                </Dropdown.Item>
              </li>
            )}
          {canAddSupplementalCustomFee && (
            <li className="w-100">
              <Dropdown.Item
                as="button"
                className="icon-button-lg"
                onClick={() => orderActions.openAddCustomFee(order)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                  className="flex-shrink-0"
                >
                  <path
                    d="M12 5V19M5 12H19"
                    stroke="#141416"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Add Custom Fee
              </Dropdown.Item>
            </li>
          )}
          {/* remove seats from season package orders, including ones that
              also link an event and ones that are not reserved/complete/plan/open */}
          {isPackageDelivery &&
            Array.isArray(order?.package_tickets) &&
            order.package_tickets.length > 0 &&
            order?.status !== ORDER_STATUS.REFUNDED &&
            order?.status !== ORDER_STATUS.RELEASED && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-refund"
                  onClick={() => orderActions.openRemoveSeats(order)}
                >
                  Remove Seats
                </Dropdown.Item>
              </li>
            )}
          {/* return unsold tickets from regular event orders */}
          {order?.event &&
            !order?.package &&
            Array.isArray(order?.tickets) &&
            order.tickets.length > 1 &&
            (order?.status === ORDER_STATUS.RESERVED ||
              order?.status === ORDER_STATUS.COMPLETE ||
              order?.status === ORDER_STATUS.COMPLETE_FROM_ASSIGNMENT ||
              order?.status === ORDER_STATUS.OPEN) && (
              <li className="w-100">
                <Dropdown.Item
                  as="button"
                  className="btn-refund"
                  onClick={() => orderActions.openRemoveEventTickets(order)}
                >
                  Remove Tickets
                </Dropdown.Item>
              </li>
            )}
          {/* show if order is 
          - is not paid for (open)
          - is past due 
          - is not an invoice payment - invoice orders use Pay by cash or check
          - is an in-person payment (cash, check, etc)
          - won't show if order is complete, is open with invoice, is payment plan, is reserved, refunded, released */}
          {(order?.status === ORDER_STATUS.OPEN ||
            order?.status === ORDER_STATUS.PAST_DUE) &&
            order?.paymentMethodType !== 'invoice' &&
            getPaymentOption(order?.paymentStatus) === 'in-person' && (
              <li className="w-100 order-3">
                <Dropdown.Item
                  as="button"
                  className="btn-complete"
                  onClick={() => orderActions.openMarkAsPaid(order, runSearch)}
                >
                  Mark as paid
                </Dropdown.Item>
              </li>
            )}
          {payInvoiceByCashOrCheck}
        </>
      )}
      {/* only show if not viewing profile */}
      {!isViewingProfile && (
        <li className="w-100 order-first">
          <Dropdown.Item
            as="button"
            className="btn-profile"
            onClick={() => orderActions.openViewProfile(order)}
          >
            View profile
          </Dropdown.Item>
        </li>
      )}
      {/* show for every order, flex packs included
          - won't show if is refunded, released */}
      {order?.status !== ORDER_STATUS.REFUNDED &&
        order?.status !== ORDER_STATUS.RELEASED && (
          <li className="w-100">
            <Dropdown.Item
              as="button"
              className="icon-button-lg btn-edit"
              onClick={() => orderActions.openViewAssociatedTo(order)}
            >
              Edit associated to
            </Dropdown.Item>
          </li>
        )}
      {/* Paid orders, orders that still owe a balance, and reserved packages
          can send or resend their delivery email. */}
      {canSendDeliveryEmail &&
        (order?.event ||
          order?.package ||
          order?.packageOrder ||
          isAccessPassDelivery ||
          order?.flex_pack ||
          order?.voucher) && (
          <li className="w-100">
            <Dropdown.Item
              as="button"
              className="btn-sendit"
              onClick={() =>
                orderActions.openConfirmSendTickets(order, runSearch)
              }
            >
              {order?.emailSent ? 'Resend ' : 'Send '}
              {isAccessPassDelivery
                ? 'Access Pass'
                : isPackageDelivery
                  ? 'package'
                  : isFlexPack && !order?.voucher
                    ? 'vouchers'
                    : 'tickets'}
            </Dropdown.Item>
          </li>
        )}
      {/* show if order is 
                      - not a flex pack and is complete 
                      - complete from exchange
                      - payment plan (installments) - partially paid
                      - still owes a balance (open, past due)
                      - reserved (holds inventory; tickets can still be printed)
                      - redeemed flex pack (voucher) and is complete
                      - flex pack purchases print vouchers instead, never tickets
                      - won't show if refunded, released */}
      {(!isFlexPack
        ? order?.status === ORDER_STATUS.COMPLETE ||
          order?.status === ORDER_STATUS.COMPLETE_FROM_EXCHANGE ||
          order?.status === ORDER_STATUS.PLAN ||
          order?.status === ORDER_STATUS.RESERVED ||
          hasUnpaidBalance
        : order?.voucher &&
          (order?.status === ORDER_STATUS.COMPLETE || hasUnpaidBalance)) && (
        <li className="w-100">
          <Dropdown.Item
            as="button"
            className="btn-print"
            onClick={() => orderActions.openViewOrder(order)}
          >
            Print tickets
          </Dropdown.Item>
        </li>
      )}
      {/* show if order is 
          - not reserved
          - not released 
          */}
      {order?.status !== ORDER_STATUS.RESERVED &&
        order?.status !== ORDER_STATUS.RELEASED && (
          <li className="w-100 order-2">
            <Dropdown.Item
              as="button"
              className="btn-view"
              onClick={() => orderActions.openInvoiceHtml(order)}
            >
              View{' '}
              {order?.status === ORDER_STATUS.OPEN ||
              order?.status === ORDER_STATUS.PLAN ||
              order?.status === ORDER_STATUS.PAST_DUE
                ? 'invoice'
                : 'receipt'}
            </Dropdown.Item>
          </li>
        )}
      {/* show if order was released (not refunded) */}
      {order?.status === ORDER_STATUS.RELEASED &&
        !isFlexPack &&
        !isSupplementalCustomFee && (
          <li className="w-100 order-last">
            <Dropdown.Item
              as="button"
              className="btn-complete"
              onClick={() => orderActions.openUndoRelease(order)}
            >
              Undo release
            </Dropdown.Item>
          </li>
        )}
      {/* show if order is 
           - no payment intent - invoice has payment intent even when not paid
          - is not paid for (open)
          - is past due
          - is not released 
          - is not refunded
          - is not reserved
          - won't show if order is paid for, refunded, released, reserved */}
      {/* Flex packs use Void order (handles refund/cancel + voucher removal). */}
      {/* Access passes always use Refund order (revokes the pass). */}
      {!isFlexPack &&
        (!hasPaymentIntent ||
          order?.status === ORDER_STATUS.OPEN ||
          order?.status === ORDER_STATUS.PAST_DUE) &&
        order?.status !== ORDER_STATUS.RELEASED &&
        order?.status !== ORDER_STATUS.REFUNDED &&
        order?.status !== ORDER_STATUS.RESERVED && (
          <li className="w-100 order-last">
            <Dropdown.Item
              as="button"
              className="btn-refund"
              onClick={() =>
                isAccessPass || isSupplementalCustomFee
                  ? orderActions.openConfirmRefund(order)
                  : orderActions.openReleaseTickets(order)
              }
            >
              {isSupplementalCustomFee
                ? 'Refund custom fee'
                : isAccessPass
                  ? 'Refund order'
                  : 'Release tickets'}
            </Dropdown.Item>
          </li>
        )}
      {/* show if order is 
          - has payment intent - invoice has payment intent even when not paid
          - is paid for 
          - not refunded 
          - not released
          - won't show if order is not paid for, in-person payment methods, refunded, released, reserved */}
      {!isFlexPack &&
        hasPaymentIntent &&
        order?.status === ORDER_STATUS.COMPLETE &&
        order?.status !== ORDER_STATUS.RELEASED &&
        order?.status !== ORDER_STATUS.REFUNDED && (
          <li className="w-100 order-last">
            <Dropdown.Item
              as="button"
              className="btn-refund"
              onClick={() => orderActions.openConfirmRefund(order)}
            >
              {isSupplementalCustomFee ? 'Refund custom fee' : 'Refund order'}
            </Dropdown.Item>
          </li>
        )}
    </EditDeleteDropdown>
  )
}
